import {
  BirdActionMenuItem,
  BirdActionMenuState,
} from '@sas-dvr/internal-va-react-core/store/birdActionMenu/BirdActionMenuState';
import { filterNulls } from './arrayUtils';
import {
  TooltipDrillController,
  AbstractAction,
  NavigationAction,
  TooltipEvent,
  NavigationActionType,
} from '@sas-dvr/ltjs-bird';
import { LocaleKey } from '@sas-dvr/internal-va-react-core/locale/LocaleKey';
import {
  MessageState,
  MessageData,
} from '@sas-dvr/internal-va-react-core/store/message/MessageState';
import { Subject, merge, Observable } from 'rxjs';
import { PayloadIdAction } from '@sas-dvr/internal-va-react-core/store/utils/action/createElementSlice';
import { StandardAction, AnyAction } from '@sas/nova-commons/action';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { StateObservable } from 'redux-observable';
import { IconProps } from '@sas/nova-commons/components/Icon';
import { withRetainedArray, ofActionCreatorType } from './rxUtils';

import { Link, GeneratedTransformation, GlobalVariables, DrillDownArrow } from '@sas/react-svg';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';

function getDrillLabel(drillController: TooltipDrillController): LocaleKey {
  const value = drillController.getPickedValue() || '';
  return {
    key: 'VisualElementActionUtil.drillLabel.fmt',
    args: { value },
  };
}

function getLinkLabel(linkAction: AbstractAction): string | LocaleKey {
  if (linkAction instanceof NavigationAction) {
    const label = linkAction.getLabel() || '';
    const linkType = linkAction.getType();

    if (linkType === NavigationActionType.SECTION) {
      return {
        key: 'DrillableTooltip.section.link.fmt',
        args: { page: label },
      };
    } else if (linkType === NavigationActionType.REPORT) {
      return {
        key: 'DrillableTooltip.report.link.fmt',
        args: { report: label },
      };
    }

    return label;
  }
  return '';
}

function getLinkIcon(linkAction: AbstractAction): IconProps['icon'] | null {
  if (linkAction instanceof NavigationAction) {
    const linkType = linkAction.getType();

    if (linkType === NavigationActionType.SECTION) {
      return Link;
    } else if (linkType === NavigationActionType.REPORT) {
      return GeneratedTransformation;
    } else if (linkType === NavigationActionType.URL) {
      return GlobalVariables;
    }
  }
  return null;
}

export function createBirdActionsHandler(
  id: string,
  myActions$: Observable<AnyAction>,
  state$: StateObservable<ReportState>
) {
  const message$: Subject<PayloadIdAction<MessageData | undefined>> = new Subject();
  const openBirdActionMenu$: Subject<StandardAction<any, any, any>> = new Subject();
  const closeBirdActionMenu$: Subject<StandardAction<any, any, any>> = new Subject();

  return {
    birdActions$: merge(message$, openBirdActionMenu$, closeBirdActionMenu$),
    birdActionsHandler(tooltipEvent: TooltipEvent, x: number, y: number) {
      const linksController = tooltipEvent ? tooltipEvent.getTooltipLinksController() : null;
      const allLinkActions = linksController ? filterNulls(linksController.getActions()) : [];
      // Report links aren't supported. Take them out of the actions list.
      const linkActions = allLinkActions
        .map((action, index) => ({ action, index })) // Store the original index for use in linksController.performAction
        .filter(({ action }) => {
          const isReportLink =
            action instanceof NavigationAction && action.getType() === NavigationActionType.REPORT;
          return !isReportLink;
        });
      const drillControllers = tooltipEvent
        ? filterNulls(tooltipEvent.getTooltipDrillControllers()).filter((drillController) => {
            // Exclude the drill controllers used for expanding/collapsing crosstab hierarchies
            return !drillController.canExpandAll() && !drillController.canCollapseAll();
          })
        : [];
      const drillableControllers = drillControllers.filter((dc) => dc.canDrillDown());
      const drillItems: BirdActionMenuItem[] = drillableControllers.map((drillController) => ({
        text: getDrillLabel(drillController),
        icon: DrillDownArrow,
      }));
      const linkItems: BirdActionMenuItem[] = linkActions.map(({ action }) => ({
        text: getLinkLabel(action),
        icon: getLinkIcon(action),
      }));
      const items = drillItems.concat(linkItems);

      const callbacks = [
        ...drillableControllers.map((drillController) => () => {
          drillController.drillDown();
        }),
        ...linkActions.map(({ index }) => () => {
          if (state$.value.bird.maximizedVisual) {
            message$.next(
              MessageState.actions.set(id, {
                text: { key: 'ReportVisualContainer.sectionLink.notAllowed.maximize.txt' },
              })
            );
          } else {
            linksController!.performAction(index); // linksController must exist if there are link actions
          }
        }),
      ];

      if (items.length === 0 && drillControllers.length > 0) {
        // There is a drill controller, but it is not drillable
        message$.next(
          MessageState.actions.set(id, {
            text: {
              key:
                drillControllers.length === 1
                  ? 'VisualElementActionUtil.hierarchyExhausted.txt'
                  : 'VisualElementActionUtil.hierarchiesExhausted.txt',
            },
          })
        );
        return true;
      } else if (drillableControllers.length === 1 && linkActions.length === 0) {
        drillableControllers[0].drillDown();
        return true;
      } else if (items.length > 1) {
        const context = (id || 'Visual Element') + ' Bird Action';
        const position = { x, y };
        const title: LocaleKey = { key: 'VisualElementActionUtil.actionMenuLabel.txt' };

        openBirdActionMenu$.next(
          BirdActionMenuState.actions.open({ context, position, title, items: items })
        );

        const menuClosed$ = myActions$.pipe(
          ofActionCreatorType<typeof BirdActionMenuState.actions.close>(
            BirdActionMenuState.actionTypes.CloseBirdActionMenu
          )
        );

        const controllersToRetain = filterNulls([linksController, ...drillControllers]);

        myActions$
          .pipe(
            ofActionCreatorType<typeof BirdActionMenuState.actions.select>(
              BirdActionMenuState.actionTypes.SelectBirdActionMenuOption
            ),
            withRetainedArray(controllersToRetain),
            filter((action) => action.payload.context === context),
            take(1),
            takeUntil(menuClosed$),
            tap((action) => {
              const index = action.payload.index;
              if (index >= 0 && index < callbacks.length) {
                callbacks[index]();
              }
              closeBirdActionMenu$.next(BirdActionMenuState.actions.close());
            })
          )
          .subscribe();

        return true;
      } else if (callbacks.length === 1) {
        callbacks[0]();
        return true;
      }

      return false;
    },
  };
}
